import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { useFormat } from '@/i18n'

export interface TimelineItem {
  id: string
  at: string | null
  /** End of a span (custody intervals); null = still there. */
  until?: string | null
  span?: boolean
  title: ReactNode
  detail?: ReactNode
  tone?: 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'accent'
}

const DOT: Record<NonNullable<TimelineItem['tone']>, string> = {
  neutral: 'bg-muted',
  ok: 'bg-ok',
  warn: 'bg-warn',
  danger: 'bg-danger',
  info: 'bg-info',
  accent: 'bg-amber',
}

/** Vertical timeline (custody, diary lines, payout tries), oldest first, IST times. */
export function Timeline({ items, empty = 'Nothing recorded yet.' }: { items: TimelineItem[]; empty?: ReactNode }) {
  const fmt = useFormat()
  if (items.length === 0) return <p className="py-2 text-[14px] text-muted">{empty}</p>
  return (
    <ol className="relative ml-1.5 border-l border-rule">
      {items.map((it) => (
        <li key={it.id} className="relative pb-4 pl-5 last:pb-0">
          <span
            aria-hidden="true"
            className={cn('absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full ring-2 ring-surface', DOT[it.tone ?? 'neutral'])}
          />
          <div className="flex flex-wrap items-baseline justify-between gap-x-3">
            <div className="text-[14px] font-semibold text-ink">{it.title}</div>
            <div className="font-mono text-[11.5px] text-muted tabular">
              {it.at ? fmt.dateTime(it.at) : '—'}
              {it.span && <> → {it.until ? fmt.dateTime(it.until) : <span className="text-ok">now</span>}</>}
            </div>
          </div>
          {it.detail && <div className="mt-0.5 text-[13px] text-ink-soft">{it.detail}</div>}
        </li>
      ))}
    </ol>
  )
}
